import axios from "axios";
import { UserResponse } from "../../types/user/user.model";
import { api } from "../axios";
import { cleanProfileText } from "../../helpers/profileText";

const LOGIN_URL = import.meta.env.VITE_LOGIN_URL;
const REGISTER_URL = import.meta.env.VITE_REGISTER_URL;
const AUTH_WITH_GOOGLE_URL = import.meta.env.VITE_AUTH_WITH_GOOGLE_URL;
const AUTH_WITH_APPLE_URL = import.meta.env.VITE_AUTH_WITH_APPLE_URL;
const USER_AUTH_STATUS = import.meta.env.VITE_USER_AUTH_STATUS;
const REMEMBER_ME = import.meta.env.VITE_REMEMBER_ME_DB_NAME;
// Same controller as sign-in/sign-up (see /api-json on the API host).
const FORGOT_PASSWORD_URL = LOGIN_URL?.replace(/sign-in$/, "forgot-password");
const RESET_PASSWORD_URL = LOGIN_URL?.replace(/sign-in$/, "reset-password");

const normEmail = (email: string) => email.trim().toLowerCase();

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
}

export const signInUser = async (data: {
  email: string;
  password: string;
  remember?: boolean;
}): Promise<LoginResponse> => {
  try {
    const res = await api.post(LOGIN_URL, {
      email: normEmail(data.email),
      password: data.password,
    });
    // Sessions always persist (refresh token) — the old checkbox is gone.
    localStorage.setItem(REMEMBER_ME, JSON.stringify(data?.remember ?? true));
    // NOTE: the profile is loaded by `useLogin` *after* the tokens are stored.
    // Calling getUserStatus() here fired an unauthenticated /auth/status
    // request (401) that tripped the refresh interceptor and logged the user
    // straight back out.
    return res.data;
  } catch (error) {
    console.error("signInUser in authApi error: ", error);
    throw error;
  }
};

export const signUpUser = async (data: {
  name: string;
  email: string;
  phone: string;
  password: string;
}): Promise<LoginResponse> => {
  // Errors are shown inline by the form (authErrors.ts), in the UI language.
  const res = await api.post(REGISTER_URL, {
    name: data.name.trim(),
    email: normEmail(data.email),
    phone_number: data.phone,
    password: data.password,
  });
  localStorage.setItem(REMEMBER_ME, JSON.stringify(true));
  return res.data;
};

/** Sends a 6-digit reset code to the email (the API answers the same whether or not the account exists). */
export const requestPasswordReset = async (email: string): Promise<void> => {
  await api.post(FORGOT_PASSWORD_URL, { email: normEmail(email) });
};

export const resetPassword = async (data: { email: string; code: string; newPassword: string }): Promise<void> => {
  await api.post(RESET_PASSWORD_URL, {
    email: normEmail(data.email),
    code: data.code.trim(),
    new_password: data.newPassword,
  });
};

export const authWithGoogle = async (
  accessToken: string,
): Promise<LoginResponse> => {
  try {
    const res = await api.post(AUTH_WITH_GOOGLE_URL, {
      access_token: accessToken,
    });
    return res.data;
  } catch (error) {
    console.error("authWithGoogle in authApi error: ", error);
    throw error;
  }
};

export const authWithApple = async (
  accessToken: string,
): Promise<LoginResponse> => {
  try {
    const res = await api.post(AUTH_WITH_APPLE_URL, {
      access_token: accessToken,
    });
    return res.data;
  } catch (error) {
    console.error("authWithApple in authApi error: ", error);
    throw error;
  }
};

export const getUserStatus = async (): Promise<UserResponse> => {
  try {
    const res = await api.get(USER_AUTH_STATUS);
    return cleanProfileText(res.data);
  } catch (error) {
    // Noise we don't want in the console:
    //  - 401 → just "not signed in" (expected for guests)
    //  - canceled / aborted → the component unmounted or the route changed
    //    mid-request (also fires twice under React 18 StrictMode in dev)
    const ax = axios.isAxiosError(error) ? error : null;
    const isExpected =
      axios.isCancel(error) ||
      ax?.code === "ERR_CANCELED" ||
      ax?.code === "ECONNABORTED" ||
      ax?.response?.status === 401;
    if (!isExpected) {
      console.error("getUserStatus in authApi error: ", error);
    }
    throw error;
  }
};
